import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserCheck, CalendarOff, ArrowRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const de = (iso) => (iso ? iso.split("-").reverse().join(".") : "—");
const TYPE = { urlaub: "Urlaub", krank: "Krank", fortbildung: "Fortbildung", dienstreise: "Dienstreise", sonstiges: "Abwesenheit" };

function AppointmentRow({ a, onAssigned }) {
  const [busy, setBusy] = useState("");
  const take = async (s) => {
    if (!window.confirm(`Termin mit ${a.client_name} am ${de(a.date)} an ${s.name} übergeben?`)) return;
    setBusy(s.user_id);
    try {
      await api.post("/vertretungsplan/assign", { appointment_id: a.appointment_id, substitute_id: s.user_id });
      toast.success(`An ${s.name} übergeben`);
      onAssigned();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };
  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-3 py-4 border-b border-slate-100 last:border-0" data-testid={`vt-appt-${a.appointment_id}`}>
      <div>
        <div className="font-mono text-xs text-slate-500">{de(a.date)} · {a.time_from || "—"}–{a.time_to || ""}</div>
        <Link to={`/avgs/${a.client_id}`} className="text-sm font-medium text-slate-900 hover:underline">{a.client_name || "Kunde"}</Link>
        <div className="text-[11px] text-slate-500">{a.topic || (a.section_id === "onboarding" ? "Onboarding / Profiling" : "Termin")} · {a.mode === "online" ? "Online" : "Vor Ort"}</div>
      </div>
      <div className="flex flex-wrap gap-2 items-start">
        {a.suggestions.length === 0 && <div className="text-xs text-rose-700" data-testid={`vt-no-sub-${a.appointment_id}`}>Niemand frei – bitte Termin verschieben</div>}
        {a.suggestions.map((s, i) => (
          <button key={s.user_id} onClick={() => take(s)} disabled={!!busy} data-testid={`vt-assign-${a.appointment_id}-${s.user_id}`}
            className={`text-left border px-3 py-2 text-xs transition-colors disabled:opacity-50 ${i === 0 ? "border-[#0a788c] bg-[#0a788c]/5 hover:bg-[#0a788c]/10" : "border-slate-200 hover:bg-slate-50"}`}>
            <div className="font-medium text-slate-900 flex items-center gap-1.5">
              {i === 0 && <Check size={12} className="text-[#0a788c]" />}{s.name}{s.is_profiler && <span className="text-[9px] uppercase tracking-overline text-[#0a788c]">Profiling</span>}
            </div>
            <div className="text-slate-500">{s.week_hours}h / {s.week_capacity}h diese Woche · {s.free_hours}h frei</div>
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-700 mt-1 flex items-center gap-1">{busy === s.user_id ? "Übergibt…" : <>Übernehmen <ArrowRight size={10} /></>}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function VertretungsplanPage() {
  const { user } = useAuth();
  const [pid, setPid] = useState("");
  const [data, setData] = useState(null);
  const load = () => api.get("/vertretungsplan", { params: pid ? { user_id: pid } : {} }).then((r) => setData(r.data)).catch(() => setData({ vacations: [], handed_over: [], staff: [] }));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line
  if (!data) return <div className="text-xs text-slate-400">Lädt…</div>;
  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto" data-testid="vertretungsplan-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><UserCheck size={13} /> Vertretungsplan</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">Wer übernimmt meine Termine?</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Alle offenen Termine während Urlaub/Abwesenheit mit Vorschlägen: zuerst Kolleg:innen aus dem Profiling, dann nach freier Kapazität – ohne Urlaub und ohne Terminüberschneidung. Nichts wird ohne Deine Bestätigung eingetragen.</p>
        </div>
        {data.staff?.length > 0 && (
          <select value={pid} onChange={(e) => setPid(e.target.value)} className="input-tech w-64" data-testid="vt-person-select">
            <option value="">{user?.name} (ich)</option>
            {data.staff.filter((s) => s.user_id !== user?.user_id).map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
          </select>
        )}
      </div>
      {data.vacations.length === 0 && (
        <div className="border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="vt-empty">
          <CalendarOff size={28} strokeWidth={1.5} className="mx-auto mb-2 text-slate-400" />
          Keine anstehende Abwesenheit für {data.person?.name || "diese Person"}. Urlaub beantragen unter <Link to="/kalender" className="underline">Kalender</Link>.
        </div>
      )}
      {data.vacations.filter((v) => !v.appointments.length && !v.clients_without_appointment.length).length > 0 && (
        <div className="text-xs text-slate-500 mb-6" data-testid="vt-empty-vacations">
          Ohne offene Termine: {data.vacations.filter((v) => !v.appointments.length && !v.clients_without_appointment.length).map((v) => `${de(v.start_date)}–${de(v.end_date)}`).join(" · ")}
        </div>
      )}
      {data.vacations.filter((v) => v.appointments.length || v.clients_without_appointment.length).map((v) => (
        <section key={v.vacation_id} className="border border-slate-200 bg-white mb-8" data-testid={`vt-vacation-${v.vacation_id}`}>
          <div className="px-5 py-4 border-b border-slate-100 vacation-hatch flex items-center justify-between flex-wrap gap-2">
            <div className="font-medium text-slate-900">{TYPE[v.type] || "Urlaub"} {de(v.start_date)} – {de(v.end_date)}</div>
            <div className="text-xs text-slate-600">{v.status === "pending" ? "Antrag offen" : "Genehmigt"} · {v.appointments.length} offene Termine</div>
          </div>
          <div className="px-5">
            {v.appointments.length === 0 && <div className="py-4 text-sm text-slate-500">Keine offenen Termine in diesem Zeitraum.</div>}
            {v.appointments.map((a) => <AppointmentRow key={a.appointment_id} a={a} onAssigned={load} />)}
          </div>
          {v.clients_without_appointment.length > 0 && (
            <div className="px-5 py-4 border-t border-slate-100 bg-amber-50" data-testid={`vt-new-clients-${v.vacation_id}`}>
              <div className="text-[10px] font-bold uppercase tracking-overline text-amber-800 mb-2">Neue Kunden ohne Termin (Start rund um die Abwesenheit)</div>
              <div className="flex flex-wrap gap-2">
                {v.clients_without_appointment.map((c) => (
                  <Link key={c.client_id} to={`/avgs/${c.client_id}`} className="text-xs border border-amber-300 bg-white px-2.5 py-1 hover:bg-amber-100">{c.name} · Start {de(c.start_date)}</Link>
                ))}
              </div>
            </div>
          )}
        </section>
      ))}
      {data.handed_over.length > 0 && (
        <section className="border border-slate-200 bg-white" data-testid="vt-handed-over">
          <div className="px-5 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Bereits übergeben</div>
          {data.handed_over.map((h) => (
            <div key={h.appointment_id} className="px-5 py-2 text-sm border-b border-slate-50 last:border-0 flex justify-between">
              <span>{de(h.date)} {h.time_from} · {h.client_name}</span><span className="text-slate-600">→ {h.coach_name}</span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
